/* Встречи: расшифровка и саммери. Общее для API, бота и мини-аппа.

   Путь один и тот же независимо от источника записи: файл или звонок →
   расшифровка (Soniox) → саммери (LLM) → готово. Названия стадий и разбор
   ссылок на созвоны нужны всем трём приложениям, поэтому лежат здесь. */

/** Порядок стадий = порядок работы; по нему считается прогресс */
export const MEETING_FLOW = ["queued", "recording", "transcribing", "summarizing", "done"];

/** Что показываем человеку, пока встреча в работе */
export const MEETING_STAGE = {
  queued: "В очереди",
  recording: "Идёт запись",
  transcribing: "Расшифровываю",
  summarizing: "Собираю саммери",
  done: "Готово",
  error: "Не получилось",
};

export const meetingBusy = (m) => !!m && m.status !== "done" && m.status !== "error";

/** Как называть встречу: имя от человека, иначе имя файла, иначе просто «Встреча».
    Название от модели попадает в title при сборке саммери, отдельной ветки не нужно. */
export const meetingTitle = (m) =>
  m?.title || m?.audioName?.replace(/\.[^.]+$/, "") || "Встреча";

/** Доля пути от 0 до 1 — для полосы прогресса */
export const meetingProgress = (m) => {
  if (!m) return 0;
  if (m.status === "done") return 1;
  const i = MEETING_FLOW.indexOf(m.status);
  return i < 0 ? 0 : i / (MEETING_FLOW.length - 1);
};

/* ── Ссылки на созвоны ──────────────────────────────────────
   Список сервисов — те, куда умеет заходить бот Recall. */

const PLATFORMS = [
  { id: "zoom", label: "Zoom", host: /(^|\.)(zoom\.us|zoomgov\.com)$/i },
  { id: "meet", label: "Google Meet", host: /(^|\.)meet\.google\.com$/i },
  { id: "teams", label: "Teams", host: /(^|\.)(teams\.microsoft\.com|teams\.live\.com)$/i },
  { id: "webex", label: "Webex", host: /(^|\.)webex\.com$/i },
];

/** Сервис по ссылке: zoom | meet | teams | webex | null */
export function meetingPlatform(url) {
  let host;
  try {
    host = new URL(String(url)).hostname;
  } catch {
    return null;
  }
  return PLATFORMS.find((p) => p.host.test(host))?.id || null;
}

export const platformLabel = (id) => PLATFORMS.find((p) => p.id === id)?.label || "Созвон";

/** Первая ссылка на созвон в тексте — по ней бот подключается к встрече */
export function findMeetingLink(text) {
  const found = String(text || "").match(/https?:\/\/[^\s<>"'`)]+/gi) || [];
  for (const raw of found) {
    // Точка или скобка в конце фразы прилипает к ссылке — отрезаем
    const url = raw.replace(/[.,;)\]]+$/, "");
    if (meetingPlatform(url)) return url;
  }
  return null;
}

/* ── Длительность ──────────────────────────────────────────── */

/** 47 мин · 1 ч 12 мин */
export function clockLen(sec) {
  const total = Math.max(0, Math.round(Number(sec) || 0));
  if (!total) return null;
  const h = Math.floor(total / 3600);
  const m = Math.round((total % 3600) / 60);
  if (!h) return `${Math.max(1, m)} мин`;
  return m ? `${h} ч ${m} мин` : `${h} ч`;
}

/* ── Реплики ────────────────────────────────────────────────
   Soniox отдаёт расшифровку токенами с номером говорящего — склеиваем их
   в реплики, чтобы читать глазами и отдавать модели с разметкой ролей. */

export function segmentsToText(segments, { withSpeakers = true } = {}) {
  const list = Array.isArray(segments) ? segments : [];
  if (!list.length) return "";
  return list
    .map((s) => (withSpeakers && s.speaker ? `${speakerName(s.speaker)}: ${s.text}` : s.text))
    .join("\n\n");
}

/** Имена говорящих Soniox не знает — нумерует их; так и показываем */
export const speakerName = (n) => (n ? `Спикер ${n}` : "Речь");
